<template>
  <div>
    <a-alert
      :message="$t('conseilClass.cloneAlert')"
      type="info"
      show-icon
      v-if="cloneAlert"
    />
    <div class="row">
      <div class="col-md-4">
        <a-select
          size="large"
          class="w-100"
          :value="selectedBtType"
          @change="selectedBtType = $event"
        >
          <a-select-option
            v-for="(value, key) in builletinTypesNames"
            :key="key"
            :value="key"
          >
            {{ value }}</a-select-option
          >
        </a-select>
      </div>
      <div class="col-md-4 d-flex">
        <label class="pr-2 pt-2">{{ $t("etablissement.niveau") }}</label>
        <a-select
          size="large"
          class="w-100"
          :value="levelSelected"
          @change="levelSelected = $event"
        >
          <a-select-option
            v-for="(value, name, index) in levelSelectData"
            :key="index"
            :value="name"
          >
            {{ value }}</a-select-option
          >
        </a-select>
      </div>
      <div class="col-md-4">
        <a-button
          type="primary"
          class="mx-auto my-2"
          style="width: 100%"
          @click="visibleClone = true"
        >
          {{ $t("bulletin.duplicateSettings") }}
        </a-button>
      </div>
    </div>
    <div class="row mb-3" v-if="!isBuiltinTemplateId(activeTemplateId)">
      <div class="col-md-6">
        <label>{{ $t("conseilClass.templateName") }}</label>
        <a-input
          v-model="templateName"
          :placeholder="$t('conseilClass.templateNamePlaceholder')"
        />
      </div>
    </div>
    <a-skeleton active v-if="loading" />
    <pv-config
      v-else-if="config.table && !loadingTemplate"
      :key="activeTemplateId"
      ref="pvConfig"
      :config="config"
      :level="levelSelected"
      :type="selectedBtType"
      :config-id="configId"
      :schoolar-year="settings.activeSchoolarYear"
      :level-select-data="levelSelectData"
      :builletin-types-names="builletinTypesNames"
      :saved-templates="allTemplates"
      :active-template-id="activeTemplateId"
      @save="saveConfig"
      @undo="undo"
      @edit-template="applyPickedTemplate"
    />
    <params-clone
      :visible="visibleClone"
      :levels="levelSelectData"
      :builletinTypesNames="builletinTypesNames"
      :current-level="levelSelected"
      :current-type="selectedBtType"
      :current-template-id="activeTemplateId"
      :current-template-label="activeTemplateLabel"
      :current-schoolar-year="settings.activeSchoolarYear"
      @hide="hideClone"
      @cloneHappened="cloneHappened"
    />
    <a-modal
      :visible="nameModalVisible"
      :title="$t('conseilClass.nameTemplateTitle')"
      :ok-text="$t('all.save')"
      :cancel-text="$t('all.cancel')"
      @ok="confirmNameModal"
      @cancel="cancelNameModal"
    >
      <p class="mb-2">{{ $t("conseilClass.nameTemplatePrompt") }}</p>
      <a-input
        v-model="pendingTemplateName"
        :placeholder="$t('conseilClass.templateNamePlaceholder')"
        @pressEnter="confirmNameModal"
      />
    </a-modal>
  </div>
</template>
<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import { getDefaultLevel, getLevels } from "@/config/LevelConstants";
import PvConfig from "./PVConfig.vue";
import ParamsClone from "./ParamsClone.vue";
import {
  DEFAULT_BUILTIN_TEMPLATE_ID,
  dedupeSavedTemplatesByLevel,
  getDefaultBuiltinTemplate,
  isBuiltinTemplateId,
  mergeTemplatesForPicker,
  resolveTemplateConfig,
} from "./builtinTemplates";
export default {
  components: { PvConfig, ParamsClone },
  name: "ConsielClass",
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    schoolType() {
      return this.activeBuilding?.type;
    },
    countryCode() {
      return this.activeBuilding?.countryCode;
    },
    levelSelectData() {
      return getLevels(this.$t.bind(this), this.schoolType, this.countryCode);
    },
    allTemplates() {
      return mergeTemplatesForPicker(this.savedTemplates);
    },
    activeTemplateLabel() {
      if (isBuiltinTemplateId(this.activeTemplateId)) {
        return `${this.$t("conseilClass.builtinDefault")} (${this.builletinTypesNames[this.selectedBtType] || this.selectedBtType})`;
      }
      return this.templateName || this.$t("conseilClass.unnamedTemplate");
    },
  },
  data() {
    const initialConfig = resolveTemplateConfig(getDefaultBuiltinTemplate());
    return {
      cloneAlert: false,
      visibleClone: false,
      config: cloneDeep(initialConfig),
      previousConfig: cloneDeep(initialConfig),
      selectedClasse: undefined,
      classes: [],
      loading: true,
      classData: [],
      levelSelected: "1",
      selectedBtType: "MP",
      configId: undefined,
      activeTemplateId: DEFAULT_BUILTIN_TEMPLATE_ID,
      templateName: "",
      savedTemplates: [],
      templatesLoading: false,
      loadingTemplate: false,
      nameModalVisible: false,
      pendingTemplateName: "",
      pendingSaveConfig: null,
      builletinTypesNames: {
        MP: this.$t("bulletin.bulletinPedagogique"),
        MT: this.$t("bulletin.bulletinSpecifique"),
        MS: this.$t("bulletin.bulletinVerySpecifique"),
      },
    };
  },
  watch: {
    levelSelected() {
      this.onContextChange();
    },
    selectedBtType() {
      this.onContextChange();
    },
  },
  async created() {
    this.loading = true;
    this.levelSelected = getDefaultLevel(this.schoolType, this.countryCode);
    await this.loadSavedTemplates();
    await this.loadLevelAssignment();
    this.loading = false;
  },
  methods: {
    isBuiltinTemplateId,
    async onContextChange() {
      if (this.loading) return;
      await this.loadLevelAssignment();
    },
    async loadSavedTemplates() {
      this.templatesLoading = true;
      try {
        const { data } = await apiClient.post("/bulletin/v2/conseil_class/filter", {
          schoolarYear: this.settings.activeSchoolarYear,
          listAll: true,
        });
        this.savedTemplates = dedupeSavedTemplatesByLevel(data || []);
      } catch (e) {
        console.info(e);
        this.savedTemplates = [];
      } finally {
        this.templatesLoading = false;
      }
    },
    async fetchLevelAssignmentRecord() {
      const { data } = await apiClient.post("/bulletin/v2/conseil_class/filter", {
        level: Number(this.levelSelected),
        type: this.selectedBtType,
        schoolarYear: this.settings.activeSchoolarYear,
      });
      return Array.isArray(data) ? data[0] : null;
    },
    async loadLevelAssignment() {
      this.loadingTemplate = true;
      try {
        const assigned = await this.fetchLevelAssignmentRecord();
        if (assigned) {
          await this.applyTemplateForEdit(assigned);
        } else {
          await this.applyTemplateForEdit(getDefaultBuiltinTemplate());
        }
      } catch (e) {
        console.info(e);
        await this.applyTemplateForEdit(getDefaultBuiltinTemplate());
      } finally {
        this.loadingTemplate = false;
      }
    },
    isTemplateNameTaken(name, excludeId = null) {
      const normalized = String(name || "")
        .trim()
        .toLowerCase();
      if (!normalized) return false;
      const exclude = excludeId != null ? String(excludeId) : null;
      return this.savedTemplates.some(
        (template) =>
          String(template._id) !== exclude &&
          String(template.name || "")
            .trim()
            .toLowerCase() === normalized
      );
    },
    resolveEditingTemplateId() {
      if (this.configId) return String(this.configId);
      if (
        this.activeTemplateId &&
        !isBuiltinTemplateId(this.activeTemplateId)
      ) {
        return String(this.activeTemplateId);
      }
      return null;
    },
    async applyTemplateForEdit(template) {
      if (!template) return;
      this.activeTemplateId = template._id;
      if (isBuiltinTemplateId(template._id)) {
        this.configId = undefined;
      } else {
        this.configId = String(template._id);
      }
      this.config = resolveTemplateConfig(template);
      this.previousConfig = cloneDeep(this.config);
      this.templateName = template.isBuiltin ? "" : template.name || "";
      await this.$nextTick();
    },
    // User picked a template from the preview dropdown. Load its settings for
    // preview/editing only; keep the current level's record as the save target
    // (activeTemplateId / configId / templateName stay put) so Save writes to
    // this level instead of overwriting the picked template.
    async applyPickedTemplate(template) {
      if (!template) return;
      this.config = resolveTemplateConfig(template);
      await this.$nextTick();
    },
    async saveConfig(config) {
      const editingId = this.resolveEditingTemplateId();
      if (editingId) {
        const current = this.savedTemplates.find(
          (t) => String(t._id) === editingId
        );
        const name =
          (this.templateName || "").trim() || current?.name || "";
        const nameChanged =
          name.toLowerCase() !==
          String(current?.name || "")
            .trim()
            .toLowerCase();
        if (nameChanged && this.isTemplateNameTaken(name, editingId)) {
          this.$message.warning(
            this.$t("conseilClass.templateNameAlreadyExists")
          );
          return;
        }
        this.configId = editingId;
        await this.persistSaveConfig(config, name);
        return;
      }
      const name = (this.templateName || "").trim();
      if (!name) {
        this.pendingSaveConfig = cloneDeep(config);
        this.pendingTemplateName = "";
        this.nameModalVisible = true;
        return;
      }
      if (this.isTemplateNameTaken(name)) {
        this.$message.warning(this.$t("conseilClass.templateNameAlreadyExists"));
        return;
      }
      await this.persistSaveConfig(config, name);
    },
    cancelNameModal() {
      this.nameModalVisible = false;
      this.pendingSaveConfig = null;
      this.pendingTemplateName = "";
    },
    async confirmNameModal() {
      const name = (this.pendingTemplateName || "").trim();
      if (!name) {
        this.$message.warning(this.$t("conseilClass.templateNameRequired"));
        return;
      }
      if (this.isTemplateNameTaken(name)) {
        this.$message.warning(this.$t("conseilClass.templateNameAlreadyExists"));
        return;
      }
      this.templateName = name;
      this.nameModalVisible = false;
      const config = this.pendingSaveConfig;
      this.pendingSaveConfig = null;
      this.pendingTemplateName = "";
      if (config) {
        await this.persistSaveConfig(config, name);
      }
    },
    async persistSaveConfig(config, name) {
      const persistConfig = cloneDeep(config);
      const level = Number(this.levelSelected);
      const schoolarYear = this.settings.activeSchoolarYear;
      const type = this.selectedBtType;
      try {
        let saved;
        const editingId = this.resolveEditingTemplateId();
        if (editingId) {
          const { data } = await apiClient.patch(
            "/bulletin/v2/conseil_class/" + editingId,
            {
              config: persistConfig,
              name,
            }
          );
          saved = data;
        } else {
          const { data } = await apiClient.put("/bulletin/v2/conseil_class/", {
            config: persistConfig,
            name,
            type,
            schoolarYear,
            level,
          });
          saved = data;
        }
        this.configId = saved._id ? String(saved._id) : undefined;
        this.activeTemplateId = saved._id ? String(saved._id) : this.activeTemplateId;
        this.templateName = saved.name || name;
        this.previousConfig = cloneDeep(persistConfig);
        this.$message.success(this.$t("conseilClass.settingsSaved"));
        await this.loadSavedTemplates();
        this.$refs.pvConfig?.syncSelectedTemplate?.();
      } catch (err) {
        console.info(err);
        this.$message.warn(this.$t("conseilClass.checkInputs"));
      }
    },
    undo() {
      this.config = cloneDeep(this.previousConfig);
      this.$nextTick(() => {
        this.$refs.pvConfig?.syncConfig?.();
      });
    },
    hideClone() {
      this.visibleClone = false;
    },
    async cloneHappened() {
      this.visibleClone = false;
      this.cloneAlert = true;
      await this.loadSavedTemplates();
      await this.loadLevelAssignment();
    },
  },
};
</script>
